import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, Upload } from "lucide-react";
import OperatorProfile from "@/components/admin/OperatorProfile";
import ServiceManagement from "@/components/admin/ServiceManagement";
import TagsCategories from "@/components/admin/TagsCategories";
import PlanBadge from "@/components/admin/PlanBadge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useAuth } from "@/hooks/useAuth";
import { Spinner } from "@/components/ui/spinner";

const OperatorAdminSimple: React.FC = () => {
  const { user, loading } = useAuth();
  const [showWelcome, setShowWelcome] = useState(() => {
    // Check if this is the first time logging in (using sessionStorage)
    const hasSeenWelcome = sessionStorage.getItem("hasSeenWelcome");
    if (!hasSeenWelcome) {
      sessionStorage.setItem("hasSeenWelcome", "true");
      return true;
    }
    return false;
  });

  const dismissWelcome = () => {
    setShowWelcome(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size="large" />
      </div>
    );
  }

  if (!user || user.role !== 'operator') {
    // Render nothing or a message if not an operator
    return null;
  }

  // Determine subscription status
  const isPremium = user.plan === 'premium';
  const isBasic = user.plan === 'basic';
  const isFree = !isPremium && !isBasic;
  const userPlan = isPremium ? 'premium' : isBasic ? 'basic' : 'free';

  // Add this helper (replace with real logic as needed)
  function isOnboardingComplete(user) {
    // TODO: Replace with real check (e.g., user.onboardingComplete or required fields)
    return user?.onboardingComplete;
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | SaunaDip</title>
        <meta name="description" content="Your sauna operator dashboard" />
      </Helmet>
      
      <div className="bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {showWelcome && (
            <Card className="mb-8 bg-gradient-to-r from-sauna-blue/20 to-sauna-blue/5 border border-sauna-blue/30 shadow-lg">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="md:flex-1">
                    <h2 className="text-2xl font-bold text-sauna-navy mb-2">Welcome to your SaunaDip Dashboard!</h2>
                    <p className="text-gray-700 mb-4">
                      Your business listing is now live in our directory. Here's what you can do now:
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <span className="text-sauna-blue font-bold">1.</span>
                        <span>Complete your profile with all business details</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-sauna-blue font-bold">2.</span>
                        <span>Choose a subscription plan to enhance visibility</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-sauna-blue font-bold">3.</span>
                        <span>Add photos, services, and more to stand out</span>
                      </li>
                    </ul>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={dismissWelcome}
                        className="bg-sauna-navy hover:bg-sauna-navy/90"
                      >
                        Get Started
                      </Button>
                      <Button 
                        asChild
                        variant="outline"
                      >
                        <Link to="/directory">
                          View Your Listing
                        </Link>
                      </Button>
                      <Button 
                        asChild
                        className="bg-sauna-orange hover:bg-sauna-orange/90"
                      >
                        <Link to="/subscription">
                          Choose a Plan
                        </Link>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="w-full md:w-1/3 rounded-lg overflow-hidden border border-gray-200">
                    <AspectRatio ratio={4/3}>
                      <div className="h-full w-full bg-sauna-beige/50 flex items-center justify-center">
                        <div className="text-center p-4">
                          <div className="bg-white/80 p-4 rounded-lg shadow-sm">
                            <h3 className="font-semibold mb-2">Your Listing Preview</h3>
                            <p className="text-sm text-gray-600">A basic listing has been created.</p>
                            <Button 
                              asChild
                              variant="link" 
                              size="sm" 
                              className="mt-2"
                            >
                              <Link to="/directory">
                                See how customers view it
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </AspectRatio>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        
          {/* Add this CTA if onboarding is incomplete */}
          {!isOnboardingComplete(user) && (
            <Card className="mb-6 border-l-4 border-sauna-orange bg-sauna-orange/10">
              <CardContent className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Upload className="h-6 w-6 text-sauna-orange" />
                  <div>
                    <h3 className="font-semibold text-sauna-orange text-lg mb-1">Complete Your Listing</h3>
                    <p className="text-gray-700 text-sm">Finish your sauna details to get your listing fully live and visible to customers.</p>
                  </div>
                </div>
                <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-bold">
                  <Link to="/sauna-wizard">Complete Now</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Welcome, {user.name}
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-gray-500">Operator dashboard</p>
                  <PlanBadge requiredPlan={userPlan} userPlan={userPlan} />
                </div>
              </div>
            </div>
            <div className="flex gap-3 ml-auto">
              <Button asChild variant="outline">
                <Link to="/operator-onboarding">
                  Quick Setup Guide
                </Link>
              </Button>
              <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90">
                <Link to="/subscription">
                  Manage Subscription
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Listing Status Card */}
          <Card className="mb-4 border-l-4 border-l-sauna-blue">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-sauna-blue/10 p-3 rounded-full">
                  <AlertCircle className="h-6 w-6 text-sauna-blue" />
                </div>
                <div>
                  <h3 className="font-medium text-lg">Your listing is live with basic visibility</h3>
                  <p className="text-gray-600 text-sm">Choose a subscription plan to enhance your presence and attract more customers</p>
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90">
                  <Link to="/directory">View Your Listing</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Analytics Overview Card (Premium Feature) */}
          <Card className="mb-4 border border-dashed border-sauna-orange/40 bg-sauna-orange/5">
            <CardContent className="p-6 flex flex-col items-center justify-center text-center">
              <div className="text-lg font-semibold text-sauna-orange mb-2">Premium Feature</div>
              <div className="text-gray-700 mb-4">Upgrade to Premium to see detailed analytics about your profile views and engagement.</div>
              <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90">
                <Link to="/pricing">Upgrade Now</Link>
              </Button>
            </CardContent>
          </Card>
          
          {/* Main Content Tabs with Card Background */}
          <Card className="mb-8 shadow-lg">
            <CardContent className="p-0">
              <div className="bg-white rounded-t-lg shadow-sm p-0">
                <Tabs defaultValue="listing" className="space-y-0">
                  <TabsList className="w-full grid grid-cols-4 h-auto rounded-t-lg overflow-hidden">
                    <TabsTrigger value="listing" className="py-2">Listing Details</TabsTrigger>
                    <TabsTrigger value="services" className="py-2">Services</TabsTrigger>
                    <TabsTrigger value="tags" className="py-2">Tags & Categories</TabsTrigger>
                    <TabsTrigger value="content" className="py-2">Photos & Content</TabsTrigger>
                  </TabsList>
                  <TabsContent value="listing" className="p-6">
                    <OperatorProfile isPremium={isPremium} isBasic={isBasic} />
                  </TabsContent>
                  <TabsContent value="services" className="p-6">
                    <ServiceManagement />
                  </TabsContent>
                  <TabsContent value="tags" className="p-6">
                    <TagsCategories isPremium={isPremium} isBasic={isBasic} />
                  </TabsContent>
                  <TabsContent value="content" className="p-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle>Photo Gallery</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {isFree ? (
                              <div className="aspect-square bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center col-span-full">
                                <div className="flex flex-col items-center">
                                  <div className="bg-sauna-blue/10 p-3 rounded-full mb-3">
                                    <Upload className="h-8 w-8 text-sauna-blue" />
                                  </div>
                                  <p className="text-sm font-medium mb-1 text-center">Gallery Images</p>
                                  <p className="text-xs text-gray-500 mb-3 text-center">Upgrade to Basic or Premium to add gallery images</p>
                                  <Button size="sm" className="bg-sauna-blue hover:bg-sauna-blue/90">Upgrade Now</Button>
                                </div>
                              </div>
                            ) : (
                              Array.from({ length: isBasic ? 3 : 5 }).map((_, i) => (
                                <div key={i} className="aspect-square bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center transition-shadow hover:shadow-md">
                                  {i === 0 && (
                                    <div className="flex flex-col items-center">
                                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                      <p className="text-sm text-gray-500">Upload Photo</p>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default OperatorAdminSimple;
